import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PageLayout from '../components/layout/PageLayout';
import LoadingSpinner from '../components/common/LoadingSpinner';
import instituteService from '../services/instituteService';
import { getAdCoverImageUrl, IMAGE_PLACEHOLDER_PATH } from '../utils/imageFallback';

const InstitutesPage = () => {
  const navigate = useNavigate();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadInstitutes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instituteService.getInstitutes({
        page_size: 48,
        ordering: '-id',
        ...(search.trim() ? { search: search.trim() } : {}),
      });
      setInstitutes(response.results || (Array.isArray(response) ? response : []));
    } catch (err) {
      console.error('Error loading institutes:', err);
      setInstitutes([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadInstitutes();
  }, [loadInstitutes]);

  const getTypeLabel = (type) => {
    const labels = {
      school: 'School',
      college: 'College',
      university: 'University',
      training: 'Training Center',
      tuition: 'Tuition Center',
      other: 'Other',
    };
    return labels[type] || 'Institute';
  };

  const formatLocation = (institute) => {
    const parts = [institute.address, institute.city].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <PageLayout title="Institutes - PakPoints">
      <Helmet>
        <title>Institutes - PakPoints</title>
        <meta name="description" content="Browse schools, colleges, and training centers on PakPoints." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Institutes</h1>
          <p className="mt-2 text-gray-600">
            Schools, colleges, universities, and training centers in our communities.
          </p>
        </div>

        <div className="mb-6">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search institutes..."
            className="w-full max-w-md min-h-[44px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="large" />
          </div>
        ) : institutes.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {institutes.map((institute) => (
              <button
                key={institute.id}
                type="button"
                onClick={() => navigate(`/institutes/${institute.id}`)}
                className="text-left bg-white rounded-lg border shadow-sm hover:shadow-md transition-shadow overflow-hidden"
              >
                <div className="h-40 bg-gray-100">
                  <img
                    src={getAdCoverImageUrl(institute)}
                    alt={institute.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                    }}
                  />
                </div>
                <div className="p-4">
                  <h2 className="font-semibold text-gray-900 line-clamp-2">{institute.name}</h2>
                  <p className="text-sm text-gray-500 mt-1">{getTypeLabel(institute.institute_type)}</p>
                  {formatLocation(institute) && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-1">{formatLocation(institute)}</p>
                  )}
                  {institute.description && (
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{institute.description}</p>
                  )}
                  {institute.society?.name && (
                    <p className="text-sm text-gray-600 mt-1 line-clamp-1">{institute.society.name}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">No institutes found.</div>
        )}
      </div>
    </PageLayout>
  );
};

export default InstitutesPage;
